<script>
import agendaService from "@/services/agenda";
import Spinner from "@/components/ui/Spinner";
import { mapGetters } from "vuex";
import leave from "@/mixins/leave";
import helperService from "@/services/helper";
import doctorService from "@/services/doctor";
import userService from "@/services/user";
import { isAgendaPricingEnabled } from "@/services/config";
import {
  WEEK_DAYS_OPTIONS,
  SLOT_ATTENTION_TYPES,
  SLOT_PERIODICITY,
} from "@/services/constants";
import { isAgendaVirtualEnabled } from "@/services/config";
import ModalInstitution from "@/components/settings/institution/ModalInstitution";
import { format, endOfDay, subMilliseconds, addMilliseconds } from "date-fns";

/**
 * @component ModalAgenda
 * @description Formulario de alta/edición de agendas. Se renderiza inline
 *              dentro del área de contenido (AgendasView oculta el listado
 *              mientras está abierto), por lo que el sidebar y el topnav
 *              quedan visibles — igual que el formulario de pacientes.
 *              Usa el look and feel de creación: header + secciones en cards.
 * @example <ModalAgenda :selectedAgenda="agenda" :fetchedInstitutions="institutions" @close="closeModal" />
 */
export default {
  name: "ModalAgenda",

  props: {
    selectedAgenda: {
      type: Object,
    },
    fetchedInstitutions: {
      type: Array,
    },
  },

  components: {
    Spinner,
    ModalInstitution,
  },

  mixins: [leave],

  data() {
    return {
      agenda: {
        institution: "",
        doctor: "",
        medicalSpecialties: [],
        startDate: new Date(),
        configurations: [
          {
            weekDays: [],
            attentionType: [],
            attentionDuration: 30,
          },
        ],
        isVisibleByPatient: true,
        scheduledUnavailabilities: [],
      },
      isPosting: false,
      doctors: [],
      medicalSpecialties: [],
      institutions: this.fetchedInstitutions,

      showDayError: false,
      dayOptions: WEEK_DAYS_OPTIONS,
      periodicityOtions: SLOT_PERIODICITY,
      attentionTypeOptions: SLOT_ATTENTION_TYPES,
      isUpdate: false,
      isLoading: false,
      showScheduledUnavailabilityError: false,
      isDeleting: false,
      isInstitutionModalVisible: false,
      isAgendaPricingEnabled: isAgendaPricingEnabled,
    };
  },

  computed: {
    ...mapGetters(["isDoctor", "user"]),

    filteredAttentionTypeOptions() {
      if (isAgendaVirtualEnabled) {
        return this.attentionTypeOptions;
      }
      return this.attentionTypeOptions.filter(
        (option) => option.value !== "virtual"
      );
    },

    isValidScheduledUnavailavilities() {
      if (this.agenda.scheduledUnavailabilities?.length) {
        return this.agenda.scheduledUnavailabilities?.every(
          (scheduledUnavailability) =>
            scheduledUnavailability.startsAt && scheduledUnavailability.endsAt
        );
      } else {
        return true;
      }
    },
  },

  watch: {
    "agenda.institution"() {
      if (!this.isUpdate) {
        if (!this.isDoctor) {
          this.agenda.doctor = "";
        }
        this.agenda.medicalSpecialties = [];

        if (this.agenda.institution) {
          this.getDoctorsByInstitution(this.agenda.institution);
        }
      }
    },

    "agenda.doctor"() {
      if (!this.isUpdate) {
        this.agenda.medicalSpecialties = [];
        if (this.agenda.doctor) {
          this.getMedicalSpecialtiesByDoctor(this.agenda.doctor);
        }
      }
    },
  },

  created() {
    if (this.selectedAgenda._id) {
      const parsedConfigurations = this.selectedAgenda.configurations.map(
        (elem) => {
          const parsedStartHour = format(new Date(elem.startHour), "HH:mm");

          let parsedEndHour = format(
            addMilliseconds(new Date(elem.endHour), 1),
            "HH:mm"
          );

          // workaround
          if (parsedEndHour === "00:00") {
            parsedEndHour = "24:00";
          }
          // workaround

          return {
            ...elem,
            startHour: parsedStartHour,
            endHour: parsedEndHour,
          };
        }
      );

      this.doctors = [this.selectedAgenda.doctor];
      this.medicalSpecialties = this.selectedAgenda.medicalSpecialties;
      this.agenda = {
        ...this.selectedAgenda,
        medicalSpecialties: this.selectedAgenda.medicalSpecialties.map(
          (medicalSpecialty) => medicalSpecialty._id
        ),
        doctor: this.selectedAgenda.doctor._id,
        institution: this.selectedAgenda.institution._id,
        configurations: parsedConfigurations,
        ...(this.selectedAgenda.startDate && {
          startDate: new Date(this.selectedAgenda.startDate),
        }),
        ...(this.selectedAgenda.endDate && {
          endDate: new Date(this.selectedAgenda.endDate),
        }),
        scheduledUnavailabilities:
          this.selectedAgenda.scheduledUnavailabilities || [],
      };
      this.isUpdate = true;
    }

    this.$bus.$on("institution-created", (institution) => {
      this.institutions.push(institution);
    });
  },

  mounted() {
    document.addEventListener("keyup", this.escape);
  },

  beforeDestroy() {
    document.removeEventListener("keyup", this.escape);
  },

  methods: {
    isAttentionTypeAvailable(attentionType) {
      return this.agenda.configurations.some((configuration) =>
        configuration.attentionType.includes(attentionType)
      );
    },

    close() {
      this.$emit("close");
    },

    getFullName(user) {
      return helperService.getFullName(user);
    },

    escape(event) {
      if (event.keyCode == 27) this.close();
    },

    parseHoursIntoDate(hours) {
      const [hour, minutes] = hours.split(":");
      const date = new Date();
      date.setHours(hour);
      date.setMinutes(minutes);
      date.setSeconds("00");
      date.setMilliseconds("000");
      return date;
    },

    submit() {
      this.createOrUpdateAgenda();
    },

    async getDoctorsByInstitution(institutionId) {
      this.isLoading = true;
      try {
        const doctors = await userService.getDoctorsByInstitution(
          institutionId
        );
        this.doctors = doctors;
      } finally {
        this.isLoading = false;
        if (this.isDoctor) {
          this.agenda.doctor = this.user._id;
        }
      }
    },

    async getMedicalSpecialtiesByDoctor(doctorId) {
      const doctorDetails = await doctorService.getDoctorDetails(doctorId);

      this.medicalSpecialties = doctorDetails.doctor?.medicalSpecialties;
    },

    async createOrUpdateAgenda() {
      const message = `La acción puede demorar unos minutos. <br/> No cierre la ventana <br/> ¿Desea continuar?`;

      this.$confirm(message, "Confirmar", {
        confirmButtonText: "Aceptar",
        cancelButtonText: "Cancelar",
        dangerouslyUseHTMLString: true,
        type: "warning",
      }).then(async () => {
        this.isPosting = true;

        const parsedConfigurations = this.agenda.configurations.map((elem) => {
          const parsedEndHour = subMilliseconds(
            this.parseHoursIntoDate(elem.endHour),
            1
          );
          const parsedStartHour = this.parseHoursIntoDate(elem.startHour);

          return {
            ...elem,
            startHour: parsedStartHour,
            endHour: parsedEndHour,
          };
        });

        const parsedAgenda = {
          ...this.agenda,
          endDate: endOfDay(this.agenda.endDate),
          configurations: parsedConfigurations,
        };

        if (!this.isUpdate) {
          try {
            const createdAgenda = await agendaService.createAgenda(
              parsedAgenda
            );
            this.$bus.$emit("agenda-created", createdAgenda);
            this.close();
          } finally {
            this.isPosting = false;
          }
        } else {
          try {
            const updatedAgenda = await agendaService.updateAgenda(
              parsedAgenda
            );
            this.$bus.$emit("agenda-updated", updatedAgenda);
            this.close();
          } finally {
            this.isPosting = false;
          }
        }
      });
    },

    getModalName() {
      if (this.isUpdate) {
        return "Actualizar Agenda";
      }
      return "Nueva Agenda";
    },

    addDay() {
      this.showDayError = false;
      const lastDay =
        this.agenda.configurations[this.agenda.configurations.length - 1];
      if (
        lastDay &&
        (!lastDay.weekDays.length ||
          !lastDay.attentionType.length ||
          !lastDay.periodicity ||
          !lastDay.startHour ||
          !lastDay.endHour ||
          !lastDay.attentionDuration)
      ) {
        this.showDayError = true;
        return;
      }
      this.agenda.configurations.push({
        weekDays: [],
        attentionType: [],
        attentionDuration: 30,
      });
    },

    removeDay(index) {
      this.showDayError = false;
      this.agenda.configurations.splice(index, 1);
    },

    addScheduledUnavailability() {
      if (this.isValidScheduledUnavailavilities) {
        this.agenda.scheduledUnavailabilities.push({
          startsAt: undefined,
          endsAt: undefined,
        });
      }
    },

    removeScheduledUnavailability(index) {
      this.agenda.scheduledUnavailabilities.splice(index, 1);
    },

    deleteAgenda() {
      this.$confirm(
        "¿Está seguro de eliminar la agenda y todas sus citas? Esto eliminará de forma permanente la agenda y todas las citas asociadas.",
        "Eliminar",
        {
          confirmButtonText: "Aceptar",
          cancelButtonText: "Cancelar",
          type: "warning",
        }
      ).then(() => {
        this.isDeleting = false;
        agendaService.delete(this.selectedAgenda._id).then(() => {
          this.$bus.$emit("agenda-deleted", this.selectedAgenda);
          this.isDeleting = false;
          this.close();
        });
      });
    },

    showInstitutionModal() {
      this.isInstitutionModalVisible = true;
    },

    closeInstitutionModal() {
      this.isInstitutionModalVisible = false;
    },
  },
};
</script>

<template lang="pug">
ValidationObserver(v-slot="{handleSubmit}")
  form.agenda-form(@submit.prevent="handleSubmit(submit)")
    .agenda-form__inner
      //- Header
      .agenda-form__header
        .agenda-form__header-left
          .agenda-form__back(@click="close")
            micon(name="arrow_back")
          h2.agenda-form__title {{ getModalName() }}
        .agenda-form__header-right
          el-button(@click="close") Cancelar
          el-button(type="primary" native-type="submit" :loading="isPosting") Guardar
      p.agenda-form__subtitle Los campos con (*) son obligatorios

      .agenda-form__content
        //- 1. Información de la agenda
        .form-section
          .form-section__title Información de la agenda
          .form-row.form-row--3col
            .form-field
              label.form-field__label
                span.required *
                | Consultorio
              ValidationProvider(name="Institución" rules="required" v-slot="{ errors }")
                el-select(
                  v-model="agenda.institution"
                  placeholder="Institución"
                  filterable
                  clearable
                  required
                  :disabled="isUpdate"
                )
                  el-option(
                    v-for="institution in institutions"
                    :key="institution._id"
                    :label="institution.name"
                    :value="institution._id"
                  )
                span.has-error {{ errors[0] }}
            .form-field(v-if="!isDoctor")
              label.form-field__label
                span.required *
                | Doctor
              ValidationProvider(name="Doctor" rules="required" v-slot="{ errors }")
                el-select(
                  v-model="agenda.doctor"
                  placeholder="Médico"
                  filterable
                  clearable
                  required
                  :no-data-text="agenda.institution ? 'No hay doctores para el hospital seleccionado' : 'Seleccione un hospital'"
                  :disabled="isUpdate"
                )
                  el-option(
                    v-for="doctor in doctors"
                    :key="doctor._id"
                    :label="getFullName(doctor)"
                    :value="doctor._id"
                  )
                span.has-error {{ errors[0] }}
            .form-field
              label.form-field__label
                span.required *
                | Especialidad
              ValidationProvider(name="Especialidad" rules="required" v-slot="{ errors }")
                el-select(
                  v-model="agenda.medicalSpecialties"
                  placeholder="Especialidad"
                  multiple
                  filterable
                  clearable
                  required
                  :no-data-text="agenda.doctor ? 'El doctor no tiene especialidades' : 'Seleccione un doctor'"
                  :disabled="isUpdate"
                )
                  el-option(
                    v-for="medicalSpecialty in medicalSpecialties"
                    :key="medicalSpecialty._id"
                    :label="medicalSpecialty.name"
                    :value="medicalSpecialty._id"
                  )
                span.has-error {{ errors[0] }}
          .form-row.form-row--3col
            .form-field
              label.form-field__label Inicia en
              ValidationProvider(name="Fecha Inicio" :rules="!isUpdate ? 'includeToday' : ''" v-slot="{ errors }" vid="startDate")
                el-date-picker(v-model="agenda.startDate" v-tooltip.bottom="'Fecha de inicio de la agenda'")
                span.has-error {{ errors[0] }}
            .form-field
              label.form-field__label Finaliza en (no obligatorio)
              ValidationProvider(name="Fecha Finalización" rules="greaterThan:@startDate" v-slot="{ errors }")
                el-date-picker(v-model="agenda.endDate" v-tooltip.bottom="'Fecha de finalización de la agenda'")
                span.has-error {{ errors[0] }}
            .form-field
              label.form-field__label
                span.required *
                | Ventana (días)
              ValidationProvider(name="Ventana" rules="required|integer|min_value:1" v-slot="{ errors }")
                el-input(v-model="agenda.windowPeriod" v-tooltip.bottom="'Período de generación de citas'")
                span.has-error {{ errors[0] }}
          .form-row
            .form-field
              el-checkbox(v-model="agenda.isVisibleByPatient" label="Citas visibles para el paciente")

        //- 2. Días de atención
        .form-section
          .form-section__title Días de atención
          .agenda-day(v-for="(day, index) in agenda.configurations")
            .form-row.form-row--3col
              .form-field
                label.form-field__label
                  span.required *
                  | Día/s {{ index + 1 }}
                ValidationProvider(:name="`Dia ${index + 1}`" rules="required" v-slot="{ errors }")
                  el-select(
                    v-model="day.weekDays"
                    placeholder="Seleccione un día"
                    filterable
                    multiple
                    clearable
                    required
                  )
                    el-option(
                      v-for="dayOption in dayOptions"
                      :key="dayOption.value"
                      :label="dayOption.name"
                      :value="dayOption.value"
                    )
                  span.has-error {{ errors[0] }}
              .form-field
                label.form-field__label
                  span.required *
                  | Periodicidad
                ValidationProvider(:name="`Periodicidad ${index + 1}`" rules="required" v-slot="{ errors }")
                  el-select(v-model="day.periodicity" required clearable)
                    el-option(
                      v-for="periodicityOption in periodicityOtions"
                      :key="periodicityOption.name"
                      :label="periodicityOption.name"
                      :value="periodicityOption.value"
                    )
                  span.has-error {{ errors[0] }}
              .form-field
                label.form-field__label
                  span.required *
                  | Modalidad
                ValidationProvider(:name="`Modalidad ${index + 1}`" rules="required" v-slot="{ errors }")
                  el-checkbox-group.agenda-day__modality(v-model="day.attentionType" required clearable)
                    el-checkbox(
                      v-for="attentionTypeOption in filteredAttentionTypeOptions"
                      :key="attentionTypeOption.name"
                      :label="attentionTypeOption.value"
                    ) {{ attentionTypeOption.name }}
                  span.has-error {{ errors[0] }}
            .form-row.form-row--3col
              .form-field
                label.form-field__label
                  span.required *
                  | Horario Inicio
                ValidationProvider(:name="`Horario Inicio ${index + 1}`" rules="required" v-slot="{ errors }" :vid="`start-${index}`")
                  el-time-select(v-model="day.startHour" :picker-options="{step:'00:15', start:'00:00', end:'24:00'}")
                  span.has-error {{ errors[0] }}
              .form-field
                label.form-field__label
                  span.required *
                  | Horario Fin
                ValidationProvider(:name="`Horario Fin ${index + 1}`" :rules="`required|greaterThan:@start-${index}`" v-slot="{ errors }")
                  el-time-select(v-model="day.endHour" :picker-options="{step:'00:15', start:'00:00', end:'24:00'}")
                  span.has-error {{ errors[0] }}
              .form-field
                label.form-field__label
                  span.required *
                  | Duración Cita (min)
                ValidationProvider(:name="`Duración Cita ${index + 1}`" rules="required|integer|min_value:1" v-slot="{ errors }")
                  el-input(v-model="day.attentionDuration")
                  span.has-error {{ errors[0] }}
            .agenda-day__remove
              a(@click="removeDay(index)") &times; Eliminar Día/s {{ index + 1 }}
          span.agenda-form__error(v-if="agenda.configurations && showDayError") Debes completar el/los día/s para poder agregar uno nuevo
          a.agenda-form__add(@click="addDay") + Agregar Día/s

        //- 3. Aranceles
        .form-section(v-if="isAgendaPricingEnabled")
          .form-section__title Aranceles
          .form-row
            .form-field
              label.form-field__label Precio virtual
              ValidationProvider(name="Precio virtual" rules="integer|min_value:1" v-slot="{ errors }")
                el-input(v-model="agenda.virtualValue" :disabled="!isAttentionTypeAvailable('virtual')")
                  i(slot="prefix" class="el-input__icon el-icon-money")
                span.has-error {{ errors[0] }}
            .form-field
              label.form-field__label Precio presencial
              ValidationProvider(name="Precio presencial" rules="integer|min_value:1" v-slot="{ errors }")
                el-input(v-model="agenda.inPersonValue" :disabled="!isAttentionTypeAvailable('in-person')")
                  i(slot="prefix" class="el-input__icon el-icon-money")
                span.has-error {{ errors[0] }}

        //- 4. Ausencias programadas
        .form-section
          .form-section__title Ausencias programadas
          .agenda-absence(v-for="(scheduledUnavailability, index) in agenda.scheduledUnavailabilities")
            .form-row
              .form-field
                label.form-field__label Inicia en
                el-date-picker(type="datetime" v-model="scheduledUnavailability.startsAt" :disabled="!!scheduledUnavailability._id")
              .form-field
                label.form-field__label Finaliza en
                el-date-picker(type="datetime" v-model="scheduledUnavailability.endsAt" :disabled="!!scheduledUnavailability._id" default-time="23:59:59")
            .agenda-day__remove(v-if="!scheduledUnavailability._id")
              a(@click="removeScheduledUnavailability(index)") &times; Eliminar ausencia programada
          el-button.agenda-form__add-btn(
            @click="addScheduledUnavailability"
            :disabled="!isValidScheduledUnavailavilities"
            v-tooltip="isValidScheduledUnavailavilities ? '' : 'Debes completar la ausencia programada para agregar una nueva'"
          ) + Agregar ausencia programada

        //- 5. Zona de peligro
        .form-section.form-section--danger(v-if="isUpdate")
          .form-section__title Zona de peligro
          el-button(type="danger" @click="deleteAgenda" :loading="isDeleting") Eliminar agenda

      //- Footer
      .agenda-form__footer
        el-button(@click="close") Cancelar
        el-button(type="primary" native-type="submit" :loading="isPosting") Guardar
</template>

<style lang="scss" scoped>
// El formulario se renderiza inline dentro del área de contenido
// (`<main>`, que ya aporta su padding), igual que el alta de pacientes:
// el sidebar y el topnav quedan visibles. Antes era una capa `fixed` a
// pantalla completa que los tapaba.
.agenda-form__inner {
  max-width: 1180px;
  margin: 0 auto;
}

// Header
.agenda-form__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}

.agenda-form__header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.agenda-form__header-right {
  display: flex;
  gap: 12px;
}

.agenda-form__back {
  cursor: pointer;
  display: flex;
  align-items: center;

  .micon {
    fill: $text-secondary-new;
    width: 22px;
    height: 22px;
  }

  &:hover .micon {
    fill: $navy;
  }
}

.agenda-form__title {
  font-size: 20px;
  font-weight: 700;
  color: $navy;
  margin: 0;
}

.agenda-form__subtitle {
  font-size: 13px;
  color: $text-secondary-new;
  margin-bottom: 24px;
}

// Sections
.form-section {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px 24px;
  margin-bottom: 20px;
  background: #fff;

  &--danger {
    border-color: #fca5a5;
  }
}

.form-section__title {
  font-size: 14px;
  font-weight: 700;
  color: $navy;
  margin-bottom: 16px;
}

// Grid rows
.form-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
}

// Fields
.form-field {
  min-width: 0;
}

.form-field__label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #6b7294;
  margin-bottom: 4px;

  .required {
    color: #ef4444;
    margin-right: 2px;
  }
}

// Bloque repetible de día / ausencia
.agenda-day,
.agenda-absence {
  padding-bottom: 16px;
  margin-bottom: 16px;
  border-bottom: 1px solid #e2e8f0;

  &:last-of-type {
    border-bottom: none;
    margin-bottom: 8px;
  }
}

.agenda-day__modality {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 16px;
  padding-top: 6px;
}

.agenda-day__remove a {
  font-size: 13px;
  color: $flamingo;
  cursor: pointer;
}

.agenda-form__add {
  display: inline-block;
  font-size: 14px;
  font-weight: 600;
  color: $navy;
  cursor: pointer;
}

.agenda-form__add-btn {
  margin-top: 4px;
}

.agenda-form__error {
  display: block;
  color: $flamingo;
  font-size: 13px;
  margin-bottom: 12px;
}

// Footer
.agenda-form__footer {
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #e2e8f0;

  .el-button {
    min-width: 200px;
    border-radius: 8px;
  }
}

// Overrides — los selects/datepickers ocupan todo el ancho de la celda
.el-select,
.el-date-editor {
  width: 100%;
}

.has-error {
  display: block;
  color: #ef4444;
  font-size: 12px;
  margin-top: 2px;
}
</style>
